import { Link } from "react-router-dom";
import { MapPin, Calendar, Users, ArrowRight, Coffee, Moon, PartyPopper, Heart, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";

interface CommunityEventCardProps {
  id?: string;
  slug?: string;
  title: string;
  date: string;
  location: string;
  distance?: string;
  participants: number;
  maxParticipants?: number | null;
  price: string;
  image: string;
  communityVibe?: string | null;
  afterActivity?: string | null;
  participationType?: string | null;
}

const vibeConfig: Record<string, { label: string; color: string; icon: React.ElementType }> = {
  relaxed: { label: "Décontracté", color: "bg-energy text-energy-foreground", icon: Coffee },
  sporty: { label: "Sportif", color: "bg-primary text-primary-foreground", icon: Sparkles },
  festive: { label: "Festif", color: "bg-accent text-accent-foreground", icon: PartyPopper },
  friendly: { label: "Convivial", color: "bg-secondary text-secondary-foreground", icon: Heart },
};

const CommunityEventCard = ({ 
  id,
  slug,
  title, 
  date, 
  location, 
  distance,
  participants, 
  maxParticipants,
  price, 
  image, 
  communityVibe,
  afterActivity,
  participationType 
}: CommunityEventCardProps) => {
  const vibeInfo = communityVibe ? vibeConfig[communityVibe] : null;
  
  // Use slug for URL if available, fallback to id
  const eventUrl = slug ? `/evenement/${slug}` : id ? `/evenement/${id}` : undefined;
  
  // Format price display based on participation type
  const getPriceDisplay = () => {
    if (participationType === 'free' || price === 'Gratuit') {
      return { text: "Gratuit", emoji: null };
    }
    if (participationType === 'voluntary') {
      return { text: "Libre", emoji: "💝" };
    }
    return { text: price, emoji: null };
  };
  
  const priceDisplay = getPriceDisplay();

  const CardWrapper = eventUrl ? Link : 'div';
  const wrapperProps = eventUrl ? { to: eventUrl } : {};

  return (
    <CardWrapper 
      {...wrapperProps as any}
      className="group relative bg-card overflow-hidden shadow-card hover:shadow-card-hover transition-all duration-500 hover:-translate-y-3 hover:rotate-1 block"
      style={{ clipPath: 'polygon(0 0, 100% 0, 100% calc(100% - 20px), calc(100% - 20px) 100%, 0 100%)' }}
    >
      {/* Speed stripe accent - accent color for community */}
      <div className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-accent via-energy to-accent opacity-0 group-hover:opacity-100 transition-opacity duration-500 z-20" />

      {/* Image */}
      <div className="relative h-56 overflow-hidden">
        <div className="absolute inset-0 bg-accent/20 mix-blend-overlay z-10 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
        <img
          src={image}
          alt={`${title} - Sortie communautaire`}
          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700 filter grayscale group-hover:grayscale-0"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-card via-card/50 to-transparent z-10" />

        {/* Distance Badge - skewed style */}
        {distance && (
          <div className="absolute top-0 left-0 bg-accent text-accent-foreground px-5 py-2 text-lg font-display font-bold uppercase tracking-wider z-20 transform -skew-x-12 -translate-x-2">
            <span className="block transform skew-x-12">~{distance}</span>
          </div>
        )}

        {/* Community Badge - top right with skew */}
        <div className="absolute top-4 right-4 bg-energy text-energy-foreground px-3 py-1 text-xs font-bold uppercase tracking-wider flex items-center gap-1 z-20 transform -skew-x-12 shadow-neon">
          <span className="transform skew-x-12">🏃 Communautaire</span>
        </div>

        {/* Vibe badge - bottom left with skew */}
        {vibeInfo && (
          <div className="absolute bottom-4 left-4 flex items-center gap-2 z-20">
            <div className={`px-2 py-1 text-xs font-bold uppercase ${vibeInfo.color} transform -skew-x-12`}>
              <span className="flex items-center gap-1 transform skew-x-12">
                <vibeInfo.icon className="w-3 h-3" />
                {vibeInfo.label}
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-6 relative z-20 bg-card">
        <h3 className="text-xl font-display text-card-foreground mb-4 group-hover:text-accent transition-colors line-clamp-1 uppercase tracking-tight">
          {title}
        </h3>

        <div className="space-y-3 mb-4">
          <div className="flex items-center gap-3 text-muted-foreground text-sm font-medium">
            <Calendar className="w-4 h-4 text-accent" />
            <span className="uppercase tracking-wide">{date}</span>
          </div>
          <div className="flex items-center gap-3 text-muted-foreground text-sm font-medium">
            <MapPin className="w-4 h-4 text-accent" />
            <span className="line-clamp-1 uppercase tracking-wide">{location}</span>
          </div>
          <div className="flex items-center gap-3 text-muted-foreground text-sm font-medium">
            <Users className="w-4 h-4 text-accent" />
            <span className="uppercase tracking-wide">
              {participants > 0 ? `${participants} coureur${participants > 1 ? 's' : ''}` : 'Sois le premier !'}
              {maxParticipants && ` / ${maxParticipants}`}
            </span>
          </div>
        </div>

        {/* After activity - styled box */}
        {afterActivity && (
          <div className="mb-4 p-3 bg-accent/10 border-l-2 border-accent">
            <p className="text-[10px] text-muted-foreground uppercase tracking-widest font-bold mb-0.5">Après la course</p>
            <p className="text-sm font-medium text-foreground">{afterActivity}</p>
          </div>
        )}

        <div className="flex items-center justify-between pt-4 border-t border-border/50 group-hover:border-accent/30 transition-colors">
          <div>
            <p className="text-[10px] text-muted-foreground uppercase tracking-widest font-bold">Participation</p>
            <p className="text-3xl font-display text-accent tracking-tighter">
              {priceDisplay.text} {priceDisplay.emoji}
            </p>
          </div>
          <Button size="icon" className="rounded-full w-12 h-12 bg-secondary hover:bg-accent hover:text-accent-foreground transition-all duration-300 group-hover:scale-110">
            <ArrowRight className="w-5 h-5" />
          </Button>
        </div>
      </div>
    </CardWrapper>
  );
};

export default CommunityEventCard;
